(ns minorodata.components.dependency-graph.page
  "Standalone dependency-graph page. A default, unclosable \"Pipelines\" tab shows
   the full graph fetched from `/api/dependency-graph/live`; right-clicking a node
   and choosing \"Open in new tab\" opens a closable tab anchored to that node
   (same endpoint, with an anchor id). Each tab lays its graph out with nested ELK
   and renders the pipeline editor's xyflow nodes. The data layer (api/events/subs)
   is registered via `minorodata.app.events` / `minorodata.app.subs`."
  (:require
   ["@xyflow/react" :refer [ReactFlowProvider]]
   ["allotment" :refer [Allotment]]
   ["lucide-react" :refer [X]]
   ["react" :as react]
   [applied-science.js-interop :as j]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.components.dependency-graph.dag :refer [subdag]]
   [minorodata.components.dependency-graph.sidebar :refer [sidebar]]
   [minorodata.components.dependency-graph.timeline :as timeline]
   [minorodata.components.log.table :refer [logs-row]]
   [minorodata.components.pipeline-editor.utils :refer [tab-icon]]
   [minorodata.components.spinner.core :as spinner]
   [minorodata.dependency-graph.timeline.api]
   [minorodata.dependency-graph.timeline.events]
   [minorodata.dependency-graph.timeline.subs]
   [minorodata.logs.model :refer [->logs-view]]
   [re-frame.core :as re-frame]
   [re-posh.core :as re-posh]
   [ui.button :as button]
   [ui.page :as page]
   [ui.table :refer [table tbody th thead tr]]
   [ui.tabs :as tabs]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(defn- tab-trigger [{:keys [id label closable? anchor-type preview?]}]
  [tabs/tabs-trigger (cond-> {:value   id
                              :variant :overlined
                              :class   (clsx "gap-2 px-3 font-normal text-xs group shrink-0"
                                             {"italic" preview?})}
                       ;; see `open-anchored-tab`
                       preview? (assoc :title           "Preview — double-click to keep this tab"
                                       :on-double-click #(dispatch [:dependency-graph.events/pin-tab id])))
   ;; entity-type icon for anchored tabs; the default "Pipelines" tab has no
   ;; anchor-type, so tab-icon returns nil and nothing is rendered.
   [tab-icon {:type anchor-type}]
   label
   (when closable?
     [button/button {:size          :icon-xs
                     :icon          X
                     :variant       :empty-transparent
                     :component     :div
                     :class         "rounded-full transition-all opacity-0 group-hover:opacity-100 -ml-1 -mr-2"
                     :on-mouse-down (fn [e] (.stopPropagation e))
                     :on-click      (fn [e]
                                      (.stopPropagation e)
                                      (dispatch [:dependency-graph.events/close-tab id]))}])])

(defn tab-panel
  "Canvas for a single tab; subscribes its own graph data keyed by `tab-id`."
  [{:keys [tab-id]}]
  (let [ready?   @(re-frame/subscribe [:dependency-graph.subs/tab-ready? tab-id])
        failed?  @(re-frame/subscribe [:dependency-graph.subs/layout-failed? tab-id])
        relayout? @(re-frame/subscribe [:dependency-graph.subs/relayouting? tab-id])
        dag-data @(re-frame/subscribe [:dependency-graph.subs/dag tab-id])]
    ;; mounts immediately; the loading steps render on the canvas until layout lands
    [:> ReactFlowProvider
     [subdag {:dag-data dag-data :loading? (not ready?) :failed? failed? :relayouting? relayout?}]]))

(defn timeline-pane
  "Bottom pane showing the run history of the pipeline a transform belongs to
   (gantt + executions table). Opens from a transform's right-click menu; closes
   via the header."
  [{:keys [master-id name]}]
  (let [;; `transform-index` is keyed by transform-id *and* master-id, so the
        ;; extra entries simply never match a gantt row's id.
        index          @(re-frame/subscribe [:dependency-graph.subs/transform-index])
        ;; group-id -> name, so a log row can name its group as a node card does.
        group-names    (into {} (map (juxt :pipeline-group/id :pipeline-group/name))
                             @(re-frame/subscribe [:pipeline-group/pull-many]))
        can-read-logs? @(re-posh/subscribe [:users.subs/has-permissions? :logs/read])]
    [:div {:class "flex flex-col h-full bg-kleene-background"}
     [:div {:class "h-10 w-full bg-kleene-secondary-01 flex items-center px-6 text-white shrink-0"}
      [:div {:class "flex-1 truncate"}
       ;; The pane covers the whole pipeline, not just the transform that opened
       ;; it — see `kioku.handlers.execution-timeline/get-pipeline`.
       [:span {:class "opacity-70"} "Pipeline runs · anchored on "]
       ;; no `name` when reopened from the URL
       [:span {:class "font-medium"} (or name (get-in index [master-id :name]))]]
      [button/button {:size     :icon
                      :variant  :empty-transparent
                      :icon     X
                      :title    "Close"
                      :class    "text-white"
                      :on-click #(dispatch [:dependency-graph.events/close-timeline])}]]
     [:div {:class "min-h-0 flex-1"}
      [timeline/timeline-page
       {:master-id      master-id
        ;; `keep`, so an index entry without a name never becomes `id -> nil` and
        ;; defeat the gantt's own fallback.
        :master->name   (into {} (keep (fn [[k v]] (when (:name v) [k (:name v)]))) index)
        :master->group  (into {} (keep (fn [[k v]]
                                         (when-let [g (get group-names (:group-id v))]
                                           [k g])))
                              index)
        :url-sync?      true
        :can-view-logs? can-read-logs?
        ;; The timeline and the logs pane share this bottom pane, so "View logs"
        ;; swaps one out for the other.
        :on-view-logs   (fn [{:keys [name transform-id]}]
                          (dispatch [:dependency-graph.events/open-logs
                                     {:name name :filter {:term transform-id}}]))
        :on-open-transform (fn [{:keys [transform-id master-id]}]
                             (let [{:keys [group-id]} (or (get index transform-id)
                                                          (get index master-id))]
                               (if group-id
                                 (dispatch [:app.events/navigate :transform-details
                                            {:group-id group-id :transform-id transform-id}])
                                 (dispatch [:app.events/add-snack :warning
                                            "That transform is not in this graph's groups"]))))
        :on-row-click   #(dispatch [:dependency-graph.events/focus-node %])
        :on-row-hover   #(dispatch [:dependency-graph.events/hover-node %])}]]]))

(def ^:private logs-pane-params {:items-per-page 50 :page 0})

(defn logs-pane'
  "Bottom pane showing recent logs for one failing entity — the same plain logs
   table the pipeline editor uses (see
   `minorodata.components.pipeline-editor.details.transform/recent-logs`), no
   filter controls. `filter` is a `->logs-view` map scoping the search (e.g.
   `{:term transform-id}` / `{:source ...}`); cleared on unmount."
  [{:keys [name filter]}]
  (let [messages @(re-posh/subscribe [:logs-pager/prop :logs-pager/messages])
        loading? @(re-posh/subscribe [:mold-http/loading? :logs.api/fetch])
        timezone @(re-posh/subscribe [:app/prop :app/timezone])]
    (react/useEffect
     (fn []
       (dispatch [:logs-view/add (->logs-view (merge logs-pane-params filter)) {:replace true}])
       (dispatch [:logs.api/fetch])
       #(dispatch-sync [:logs-view/delete]))
     #js [])
    [:div {:class "flex flex-col h-full bg-kleene-background"}
     [:div {:class "h-10 w-full bg-kleene-secondary-01 flex items-center px-6 text-white shrink-0"}
      [:div {:class "flex-1 truncate"}
       [:span {:class "opacity-70"} "Logs: "]
       [:span {:class "font-medium"} name]]
      [button/button {:size     :icon
                      :variant  :empty-transparent
                      :icon     X
                      :title    "Close"
                      :class    "text-white"
                      :on-click #(dispatch [:dependency-graph.events/close-logs])}]]
     [:div {:class "min-h-0 flex-1 overflow-auto"}
      (cond
        loading?
        [:div {:class "flex gap-2 items-center px-10 py-6"}
         [spinner/spinner-lucide] "Loading logs..."]

        (empty? messages)
        [typography/p {:class "text-kleene-text-secondary px-10 py-6"} "No recent logs"]

        :else
        [table {:data-cy "logs-table" :sticky-header true}
         [thead
          [tr {:sticky true}
           [th {:style {:width "180px" :height "52px"} :class "border-r"}
            [:span "Date"
             [:div {:class "-mt-1"}
              [:span {:class "text-xs text-kleene-dark-05 dark:text-kleene-dark-03"} timezone]]]]
           [th {:style {:width "80px"} :class "border-r"} "Type"]
           [th {:class "border-r"} "Message"]
           [th {:style {:width "80px"}} "Task"]]]
         [tbody {}
          (for [message-id messages]
            ^{:key message-id}
            [logs-row {:message-id   message-id
                       :skip-columns ["source-name" "name"]
                       :interactive  false}])]])]]))

(defn page' [{:keys [tabs active-tab-id timeline logs collapsed? focus-id timeline-id]}]
  ;; Seeded with the mount-time `?focus=`, which `init` below already owns (held as
  ;; `:pending-focus` until the graph is laid out), so the effect that watches it
  ;; only ever reacts to a LATER one.
  (let [handled (react/useRef focus-id)]
    (react/useEffect
     (fn []
       ;; read here rather than inside `init`, so the route is pulled through a
       ;; subscription in a reactive context instead of an event handler
       (dispatch [:dependency-graph.events/init {:focus-id focus-id :timeline-id timeline-id}])
       #(dispatch [:dependency-graph.events/cleanup]))
     #js [])

    ;; `?focus=` arriving while the page is already open: a query-param-only
    ;; navigation re-renders the screen rather than remounting it, so `init` never
    ;; runs again and the link silently did nothing. Reachable — the SQL console and
    ;; Kai modals are mounted app-wide and both link here with `?focus=`. The graph
    ;; is loaded by now, so this centres directly instead of via `:pending-focus`.
    (react/useEffect
     (fn []
       (when (and focus-id (not= focus-id (j/get handled :current)))
         (dispatch [:dependency-graph.events/focus-linked-node focus-id]))
       (j/assoc! handled :current focus-id)
       js/undefined)
     #js [focus-id]))

  [page/page-root {:class "overflow-hidden"}
   [page/page-content {:variant :border}
    [:> Allotment
     [:> Allotment.Pane {:min-size 320}
      [:div {:class "relative h-full"}
       [:> Allotment {:vertical true}
        [:> Allotment.Pane {:min-size 150}
         (if (empty? tabs)
           [page/page-loading-screen]
           [tabs/tabs-root {:value           active-tab-id
                            :variant         :overlined
                            :class           "grid grid-cols-1 grid-rows-[auto_1fr] h-full overflow-hidden"
                            :activation-mode "manual"
                            :on-value-change #(dispatch [:dependency-graph.events/select-tab %])}
            [tabs/tabs-list {:variant :overlined
                             :class   "overflow-x-auto scrollbar-thin pr-12"}
             (for [tab tabs]
               ^{:key (:id tab)}
               [tab-trigger tab])]
            (for [{:keys [id]} tabs]
              ^{:key id}
              [tabs/tabs-content {:value id
                                  :class "relative overflow-hidden h-full"}
               [tab-panel {:tab-id id}]])])]

        [:> Allotment.Pane {:snap           true
                            :visible        (or (some? timeline) (some? logs))
                            :preferred-size "45%"
                            :min-size       180}
         (cond
           timeline ^{:key (str "timeline-" (:master-id timeline))} [timeline-pane timeline]
           logs     ^{:key (str "logs-" (:name logs))}              [:f> logs-pane' logs])]]]]

     [:> Allotment.Pane {:min-size       0
                         :preferred-size 360
                         :snap           true
                         :visible        (not collapsed?)}
      [sidebar]]]]])

(defn page []
  (let [tabs          @(re-frame/subscribe [:dependency-graph.subs/tabs])
        active-tab-id @(re-frame/subscribe [:dependency-graph.subs/active-tab-id])
        timeline      @(re-frame/subscribe [:dependency-graph.subs/timeline])
        logs          @(re-frame/subscribe [:dependency-graph.subs/logs])
        collapsed?    @(re-frame/subscribe [:dependency-graph.subs/failures-collapsed?])
        query         @(re-frame/subscribe [:app-route/prop :app-route/query])
        ;; `/dag?focus=<master-id>` — the entity a link from elsewhere in the app named
        focus-id      (:focus query)
        ;; `?tl=<master-id>`, the open execution timeline
        timeline-id   (:tl query)]
    [:f> page' {:tabs          tabs
                :active-tab-id active-tab-id
                :timeline      timeline
                :logs          logs
                :collapsed?    collapsed?
                :focus-id      focus-id
                :timeline-id   timeline-id}]))
